import { Component, DoCheck, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-questions-inline',
  templateUrl: './questions-inline.component.html',
  styleUrls: ['./questions-inline.component.sass']
})
export class QuestionsInlineComponent implements OnChanges, DoCheck {
  @Input() questions: any[] = [];
  @Input() record: any = {};
  @Input() field: any;
  @Output() onChange = new EventEmitter<any[]>();

  resolvedQuestions: any[] = [];
  availableOptions: any[] = [];
  isResolving: boolean = false;
  includeUntagged: boolean = false;

  private miniGameTypeCache: any[] | null = null;
  private creatureTypeNameMap: Map<string, string> | null = null;
  private variantNameMap: Map<string, string> | null = null;
  private _optionsLoading = false;
  private _filterKey: string = '';

  constructor(private dataService: DataService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['questions']) {
      this.resolveQuestions(this.questions || []);
    }
    if (changes['record']) {
      this.loadAvailableOptions();
    }
  }

  // Reloads options when type/creature/variant change on the record object in-place
  ngDoCheck() {
    if (this._optionsLoading) return;
    const key = [
      this.record?.type ?? '',
      this.record?.creatureType_ref?._id || this.record?.creatureType_ref || '',
      (this.record?.variant_ref || []).map((v: any) => v._id || v).join(','),
      this.includeUntagged,
    ].join('|');
    if (key !== this._filterKey) {
      this._filterKey = key;
      this.loadAvailableOptions();
    }
  }

  onIncludeUntaggedChange() {
    // Reset key so ngDoCheck picks up the new includeUntagged value even if loading
    this._filterKey = '';
    if (!this._optionsLoading) {
      this.loadAvailableOptions();
    }
  }

  private async resolveQuestions(input: any[]) {
    if (!input.length) {
      this.resolvedQuestions = [];
      return;
    }

    const stringIds = input.filter((q: any) => typeof q === 'string');
    const objects = input.filter((q: any) => typeof q === 'object' && q !== null);

    if (stringIds.length === 0) {
      this.resolvedQuestions = objects;
      return;
    }

    // Build lookup from already-resolved objects and existing resolvedQuestions cache
    const existingById = new Map(objects.map((q: any) => [q._id?.toString(), q]));
    for (const q of this.resolvedQuestions) {
      if (q._id && !existingById.has(q._id.toString())) {
        existingById.set(q._id.toString(), q);
      }
    }

    const missingIds = stringIds.filter((id: string) => !existingById.has(id));

    if (missingIds.length === 0) {
      // All IDs already in cache — no API call needed (covers the emit round-trip)
      this.resolvedQuestions = input.map((q: any) => {
        const id = typeof q === 'string' ? q : q._id?.toString();
        return existingById.get(id) ?? { _id: id, question: '', id: null };
      });
      return;
    }

    this.isResolving = true;
    try {
      const result = await this.dataService.findManyDynamic('questions', {
        query: { _id: { $in: missingIds } },
        select: 'question id _id',
        pageSize: missingIds.length + 10,
      });
      const fetched = Array.isArray(result) ? result : (result?.records ?? result?.data ?? []);
      for (const q of fetched) {
        if (q._id) existingById.set(q._id.toString(), q);
      }

      this.resolvedQuestions = input.map((q: any) => {
        const id = typeof q === 'string' ? q : q._id?.toString();
        return existingById.get(id) ?? { _id: id, question: '', id: null };
      });
    } finally {
      this.isResolving = false;
    }
  }

  async loadAvailableOptions() {
    if (this._optionsLoading) return;
    this._optionsLoading = true;
    try {
      const filter = await this.buildFilter();
      const result = await this.dataService.findManyDynamic('questions', {
        query: filter,
        select: 'question id _id',
        sort: { id: 1 },
        pageSize: 500,
      });
      const all = Array.isArray(result) ? result : (result?.records ?? result?.data ?? []);
      // Pool reflects the CURRENT filter only. Selected questions that fall
      // outside the filter still render as chips (driven by resolvedQuestions),
      // so we must not re-inject them here — doing so lets "select all" re-pick
      // questions from filters the user has since removed.
      const byId = new Map(all.map((q: any) => [q._id?.toString(), q]));
      this.availableOptions = [...byId.values()];
    } catch {
      this.availableOptions = [...this.resolvedQuestions];
    } finally {
      this._optionsLoading = false;
    }
  }

  private async buildFilter(): Promise<any> {
    if (this.includeUntagged) return { enabled: true };

    const clauses: any[] = [{ enabled: true }];
    const mgType = this.record?.type;
    const ctRef = this.record?.creatureType_ref;
    const vRef = this.record?.variant_ref;

    if (mgType != null) {
      const typeId = await this.resolveMiniGameTypeId(mgType);
      if (typeId) clauses.push({ minigame_type: typeId });
    }

    if (ctRef) {
      const ctId = await this.resolveRefId(ctRef, () => this.loadCreatureTypeMap());
      if (ctId) clauses.push({ creatureType_ref: ctId });
    }

    // variant_ref on the question is a single ObjectId; the minigame can have one or many variants.
    // Collect every defined variant id (single value or array).
    const variantIds: string[] = [];
    if (Array.isArray(vRef)) {
      const ids = (await Promise.all(
        vRef.map((v: any) => this.resolveRefId(v, () => this.loadVariantMap())),
      )).filter((id): id is string => Boolean(id));
      variantIds.push(...ids);
    } else if (vRef) {
      const vId = await this.resolveRefId(vRef, () => this.loadVariantMap());
      if (vId) variantIds.push(vId);
    }

    // When variant(s) are defined, include questions tagged for any of the defined
    // variants AND questions with no variant, while excluding questions tagged for
    // other (non-matching) variants. Single vs. multiple variant behaves identically
    // here — the "serve only for the matching creature" rule is applied at runtime.
    // (A `variant_ref: null` clause matches both null and missing fields in Mongo.)
    if (variantIds.length) {
      clauses.push({
        $or: [
          { variant_ref: { $in: variantIds } },
          { variant_ref: null },
        ],
      });
    }

    return clauses.length > 1 ? { $and: clauses } : clauses[0] ?? {};
  }

  private isObjectId(value: any): boolean {
    return typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value);
  }

  /**
   * Resolves a ref value to a valid ObjectId string.
   * Handles populated objects (`{ _id }`), raw ObjectId strings, and plain
   * name strings (e.g. "Butterfly") — the latter are looked up via the API so
   * they never reach Mongo as an un-castable value. Returns null if unresolvable.
   */
  private async resolveRefId(value: any, loadMap: () => Promise<Map<string, string>>): Promise<string | null> {
    if (!value) return null;
    if (typeof value === 'object') return value._id?.toString() ?? null;
    if (this.isObjectId(value)) return value;
    const map = await loadMap();
    return map.get(String(value).trim().toLowerCase()) ?? null;
  }

  private async loadCreatureTypeMap(): Promise<Map<string, string>> {
    if (!this.creatureTypeNameMap) {
      this.creatureTypeNameMap = await this.loadNameMap('creaturetypes');
    }
    return this.creatureTypeNameMap;
  }

  private async loadVariantMap(): Promise<Map<string, string>> {
    if (!this.variantNameMap) {
      this.variantNameMap = await this.loadNameMap('variants');
    }
    return this.variantNameMap;
  }

  private async loadNameMap(controller: string): Promise<Map<string, string>> {
    const result = await this.dataService
      .getAllOfTypeAsync(controller, { query: {}, select: 'name _id' })
      .catch(() => []);
    const list = Array.isArray(result) ? result : [];
    return new Map(list.map((r: any) => [r.name?.toLowerCase(), r._id]));
  }

  private async resolveMiniGameTypeId(value: number): Promise<string | null> {
    if (!this.miniGameTypeCache) {
      const result = await this.dataService.getAllOfTypeAsync(
        'minigame-types',
        { query: {}, select: 'value _id' }
      ).catch(() => []);
      this.miniGameTypeCache = Array.isArray(result) ? result : [];
    }
    return this.miniGameTypeCache.find((t: any) => t.value === value)?._id ?? null;
  }

  onSelectionChange(value: any[]) {
    this.resolvedQuestions = value || [];
    this.onChange.emit(this.resolvedQuestions.map((q: any) => q._id?.toString() || q));
  }

  removeQuestion(index: number) {
    const updated = [...this.resolvedQuestions];
    updated.splice(index, 1);
    this.resolvedQuestions = updated;
    this.onChange.emit(updated.map((q: any) => q._id?.toString() || q));
  }

  getQuestionLabel(q: any): string {
    return q?.question || '';
  }
}
